import { Box, Typography } from '@mui/material'
import { Page } from 'components/Page'
import { DataTable } from 'components/Shared/DataTable'
import { Colors } from 'components/Theme'
import { type GridColDef } from '@mui/x-data-grid'
import { isPresent } from '@jjvgaming/player-payback-library'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import _, { flatten, some, uniq } from 'lodash'
import { useGetMultipleRewardsByLicensedEstablishmentId } from 'hooks/api/useGetMultipleRewardsByLicensedEstablishmentId'
import { useGetMultipleRewardsByOrganizationId } from 'hooks/api/useGetMultipleRewardsByOrganizationId'
import { useGetMultipleRewardsByCorporateAccountId } from 'hooks/api/useGetMultipleRewardsByCorporateAccountId'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { PageHeader } from 'components/Shared/PageHeader'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import NoPatronsCheckedInIcon from 'assets/NoPatronsCheckedIn.png'
import { getLEIdsByPermission } from 'utils/permissions'
import { useSetMe } from 'stores/useMeQuery'

export const RewardsPage = () => {
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const navigate = useNavigate()

  const leRewardsQuery = useGetMultipleRewardsByLicensedEstablishmentId({
    licensedEstablishmentIds:
      meQuery?.data?.licensedEstablishments
        ?.filter((x) => x.corporateAccountId === selectedCorporateAccountId)
        .map((x) => x.id) ?? [],
  })

  const orgRewardsQuery = useGetMultipleRewardsByOrganizationId({
    organizationIds:
      meQuery?.data?.organizations
        ?.filter((x) => x.corporateAccountId === selectedCorporateAccountId)
        .map((x) => x.id) ?? [],
  })

  const caRewardsQuery = useGetMultipleRewardsByCorporateAccountId({
    corporateAccountIds:
      meQuery?.data?.user?.licensedEstablishmentUserCorporateAccounts
        ?.filter(
          (x) =>
            x.allowed && x.corporateAccountId === selectedCorporateAccountId
        )
        .map((x) => x.corporateAccountId) ?? [],
  })

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'rewardsPermission'
  )

  // Filter data by permission
  const rewards = useMemo(() => {
    const orgRewards = uniq(flatten(orgRewardsQuery.map((x) => x.data)))
    const leRewards = uniq(flatten(leRewardsQuery.map((x) => x.data))).filter(
      (x) => leIdsByPermission.includes(Number(x?.licensedEstablishmentId))
    )
    const caRewards = uniq(flatten(caRewardsQuery.map((x) => x.data)))

    const corporateRewards = caRewards.filter(
      (reward) => reward?.type === 'CorporateAccount'
    )
    const locationRewards = leRewards.filter(
      (reward) => reward?.type === 'LicensedEstablishment'
    )
    const organizationRewards = orgRewards.filter(
      (reward) => reward?.type === 'Organization'
    )

    return { corporateRewards, organizationRewards, locationRewards }
  }, [leRewardsQuery, orgRewardsQuery, caRewardsQuery])

  if (
    meQuery.isPending ||
    some(leRewardsQuery, (x) => x.isPending) ||
    some(caRewardsQuery, (x) => x.isPending) ||
    some(orgRewardsQuery, (x) => x.isPending)
  ) {
    return (
      <Page>
        <ActivityIndicator />
      </Page>
    )
  }

  if (meQuery.isError) {
    return <Typography>Something went wrong</Typography>
  }

  const getColumns = (): GridColDef[] => [
    { field: 'name', headerName: 'Program Name', minWidth: 480, flex: 4 },
    {
      field: 'active',
      headerName: 'Status',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => {
        const active = params.value
        const promoBackgroundColor: Record<string, string> = {
          ACTIVE: Colors.rewardsStatus.background.Active,
          INACTIVE: Colors.rewardsStatus.background.Inactive,
        }

        return (
          <Box
            style={{
              backgroundColor:
                promoBackgroundColor[active ? 'ACTIVE' : 'INACTIVE'],
              color: active
                ? Colors.rewardsStatus.text.Active
                : Colors.rewardsStatus.text.Inactive,
              fontWeight: 'bold',
              borderRadius: '4px',
              paddingTop: '3px',
              paddingBottom: '3px',
              display: 'inline-block',
              minWidth: '84px',
              textAlign: 'center',
            }}
          >
            {active ? 'ACTIVE' : 'INACTIVE'}
          </Box>
        )
      },
    },
  ]

  return (
    <Page
      header={
        <Box>
          <PageHeader
            title="Reward Programs"
            isSecondary={true}
            headerBgColor="white"
          />
        </Box>
      }
    >
      {meQuery.isPending && <ActivityIndicator />}
      {meQuery.isError && <ErrorIndicator />}
      {leIdsByPermission.length === 0 ? (
        <NoDataIndicator
          noImage
          noDataMessage="Your current permissions do not allow access to view reward programs. To obtain access, please contact your Account Manager for assistance."
        />
      ) : (
        <>
          {leIdsByPermission.length !== 0 && (
            <>
              {rewards?.corporateRewards.length === 0 &&
                rewards?.locationRewards.length === 0 &&
                rewards?.organizationRewards.length === 0 && (
                  <Box>
                    <NoDataIndicator
                      imageSource={NoPatronsCheckedInIcon}
                      noDataMessage="No Reward Programs in place, contact your Account Manager
                      to discuss options"
                    />
                  </Box>
                )}
              {isPresent(rewards.corporateRewards) &&
                rewards.corporateRewards.length > 0 && (
                  <Box py={3}>
                    <Typography pb={3} variant="h3">
                      Corporate Reward Programs
                    </Typography>
                    <DataTable
                      columns={getColumns()}
                      rows={_.orderBy(rewards.corporateRewards, 'name')}
                      onRowClick={async (row) => {
                        await navigate(`/RewardsProgram/${row.id}`)
                      }}
                    />
                  </Box>
                )}

              {isPresent(rewards.organizationRewards) &&
                rewards.organizationRewards.length > 0 && (
                  <Box pt={3}>
                    <Typography pb={3} variant="h3">
                      Organization Reward Programs
                    </Typography>
                    <DataTable
                      columns={getColumns()}
                      rows={_.orderBy(rewards.organizationRewards, 'name')}
                      onRowClick={async (row) => {
                        await navigate(`/RewardsProgram/${row.id}`)
                      }}
                    />
                  </Box>
                )}

              {isPresent(rewards.locationRewards) &&
                rewards.locationRewards.length > 0 && (
                  <Box py={6}>
                    <Typography pb={3} variant="h3">
                      Location Reward Programs
                    </Typography>
                    <DataTable
                      columns={getColumns()}
                      rows={_.orderBy(rewards.locationRewards, 'name')}
                      onRowClick={async (row) => {
                        await navigate(`/RewardsProgram/${row.id}`)
                      }}
                    />
                  </Box>
                )}
            </>
          )}
        </>
      )}
    </Page>
  )
}
